import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div data-testid="reset-no-token">
          <h2 className="font-display text-2xl font-bold text-slate-900">Invalid link</h2>
          <p className="text-slate-500 text-sm mt-2">This reset link is missing or invalid. Please request a new one.</p>
          <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 mt-6"
            data-testid="request-new-link">Request a new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      {done ? (
        <div data-testid="reset-done">
          <div className="h-11 w-11 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="text-green-600" size={22} />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Password updated</h2>
          <p className="text-slate-500 text-sm mt-2">You can now sign in with your new password. Redirecting…</p>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 mt-6"
            data-testid="go-to-login-link">Go to sign in</Link>
        </div>
      ) : (
        <>
          <h2 className="font-display text-2xl font-bold text-slate-900">Choose a new password</h2>
          <p className="text-slate-500 text-sm mt-1 mb-7">Enter a new password for your account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">New password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters" className="mt-1.5 h-11" data-testid="reset-password-input" />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-slate-500">Confirm password</Label>
              <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••" className="mt-1.5 h-11" data-testid="reset-confirm-input" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full h-11 text-white font-medium" style={{ background: "var(--cp-primary)" }}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
            </Button>
          </form>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 mt-6"
            data-testid="back-to-login-link">
            <ArrowLeft size={15} /> Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}
